import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import { Download, FileText, CheckCircle, Clock, Users, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { exportPMRReport } from '../../utils/pdfExport';

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [officerFilter, setOfficerFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetail, setReportDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [officers, setOfficers] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [activeTab, setActiveTab] = useState('reports'); // reports, performance

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (officerFilter) params.officer_id = officerFilter;
      if (districtFilter) params.district_id = districtFilter;
      if (sectorFilter) params.sector = sectorFilter;
      const response = await api.get('/reports', { params });
      setReports(response.data.reports);
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchOfficers = async () => {
    try {
      const response = await api.get('/users', { params: { role: 'Monitoring Officer' } });
      setOfficers(response.data.users);
    } catch (err) {
      console.error('Failed to load officers');
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await api.get('/districts');
      setDistricts(response.data.districts);
    } catch (err) {
      console.error('Failed to load districts');
    }
  };

  const fetchReportDetail = async (id) => {
    try {
      setLoadingDetail(true);
      const response = await api.get(`/reports/${id}`);
      setReportDetail(response.data);
    } catch (err) {
      toast.error('Failed to load report detail');
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchOfficers();
    fetchDistricts();
  }, [statusFilter, dateFrom, dateTo, officerFilter, districtFilter, sectorFilter]);

  const handleViewReport = (report) => {
    setSelectedReport(report);
    fetchReportDetail(report.id);
  };

  const handleExportPMR = async (report) => {
    try {
      const response = await api.get(`/reports/${report.id}`);
      exportPMRReport(response.data.report, response.data.sections);
      toast.success('PDF exported successfully!');
    } catch (err) {
      toast.error('Failed to export PDF');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleDateString('en-PK', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleTimeString('en-PK', {
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  // Officer Performance Summary
  const officerPerformance = officers.map(officer => {
    const officerReports = reports.filter(r => r.officer_name === officer.full_name);
    const submitted = officerReports.filter(r => r.status === 'Submitted').length;
    const draft = officerReports.filter(r => r.status === 'Draft').length;
    const total = officerReports.length;
    const rate = total > 0 ? Math.round((submitted / total) * 100) : 0;
    return {
      id: officer.id,
      name: officer.full_name,
      district: officer.district_name || '—',
      total,
      submitted,
      draft,
      rate
    };
  }).filter(o => o.total > 0);

  return (
    <Layout title="Visit Reports">

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-blue-600">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-500 text-sm">Total Reports</p>
              <p className="text-3xl font-bold text-gray-800">{reports.length}</p>
            </div>
            <FileText size={32} className="text-blue-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-green-600">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-500 text-sm">Submitted</p>
              <p className="text-3xl font-bold text-gray-800">
                {reports.filter(r => r.status === 'Submitted').length}
              </p>
            </div>
            <CheckCircle size={32} className="text-green-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-yellow-500">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-500 text-sm">Draft</p>
              <p className="text-3xl font-bold text-gray-800">
                {reports.filter(r => r.status === 'Draft').length}
              </p>
            </div>
            <Clock size={32} className="text-yellow-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-purple-600">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-500 text-sm">Active Officers</p>
              <p className="text-3xl font-bold text-gray-800">{officerPerformance.length}</p>
            </div>
            <Users size={32} className="text-purple-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('reports')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition
            ${activeTab === 'reports'
              ? 'bg-blue-800 text-white'
              : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}
        >
          <FileText size={16} />
          All Reports
        </button>
        <button
          onClick={() => setActiveTab('performance')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition
            ${activeTab === 'performance'
              ? 'bg-blue-800 text-white'
              : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}
        >
          <TrendingUp size={16} />
          Officer Performance
        </button>
      </div>

      {/* All Reports Tab */}
      {activeTab === 'reports' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-xl shadow p-4 mb-6">
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Officer</label>
                <select
                  value={officerFilter}
                  onChange={(e) => setOfficerFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Officers</option>
                  {officers.map(o => (
                    <option key={o.id} value={o.id}>{o.full_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">District</label>
                <select
                  value={districtFilter}
                  onChange={(e) => setDistrictFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Districts</option>
                  {districts.map(d => (
                    <option key={d.id} value={d.id}>{d.district_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Sector</label>
                <select
                  value={sectorFilter}
                  onChange={(e) => setSectorFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Sectors</option>
                  <option value="Public">Public</option>
                  <option value="Private">Private</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Draft">Draft</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                <input type="date" value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                <input type="date" value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <button
                onClick={() => {
                  setStatusFilter(''); setDateFrom(''); setDateTo('');
                  setOfficerFilter(''); setDistrictFilter(''); setSectorFilter('');
                }}
                className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50"
              >
                Clear All
              </button>
              <button
                onClick={() => toast.success('Bulk export coming soon!')}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 ml-auto"
              >
                <Download size={16} />
                Export All
              </button>
            </div>
          </div>

          {/* Reports Table */}
          <div className="bg-white rounded-xl shadow">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">#</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Officer</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Institution</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">District</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Visit Date</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Book No</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">PMR No</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Sector</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="text-center py-8 text-gray-500">Loading reports...</td>
                    </tr>
                  ) : reports.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-8 text-gray-500">No reports found</td>
                    </tr>
                  ) : (
                    reports.map((report, index) => (
                      <tr key={report.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                        <td className="px-4 py-3 font-medium text-gray-800">{report.officer_name}</td>
                        <td className="px-4 py-3 text-gray-600">{report.institution_name}</td>
                        <td className="px-4 py-3 text-gray-600">{report.district_name}</td>
                        <td className="px-4 py-3 text-gray-600">{formatDate(report.visit_date)}</td>
                        <td className="px-4 py-3 text-gray-600">{report.book_no || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{report.pmr_no || '—'}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium
                            ${report.training_sector === 'Public'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-purple-100 text-purple-700'}`}>
                            {report.training_sector}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium
                            ${report.status === 'Submitted'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'}`}>
                            {report.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => handleViewReport(report)}
                              className="text-blue-600 hover:text-blue-800 text-xs font-medium">View</button>
                            <span className="text-gray-300">|</span>
                            <button onClick={() => handleExportPMR(report)}
                              className="text-green-600 hover:text-green-800 text-xs font-medium">PDF</button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
              Total: {reports.length} reports
            </div>
          </div>
        </>
      )}

      {/* Officer Performance Tab */}
      {activeTab === 'performance' && (
        <div className="bg-white rounded-xl shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="font-bold text-gray-800 flex items-center gap-2">
              <TrendingUp size={18} className="text-blue-600" />
              Officer Performance Summary
            </h2>
            <p className="text-sm text-gray-500">
              Based on {reports.length} total reports
            </p>
          </div>
          <div className="overflow-x-auto">
            {officerPerformance.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No report data available yet
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">#</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Officer Name</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">District</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Total Reports</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Submitted</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Draft</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Submit Rate</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {officerPerformance
                    .sort((a, b) => b.submitted - a.submitted)
                    .map((officer, index) => (
                    <tr key={officer.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{officer.name}</td>
                      <td className="px-4 py-3 text-gray-600">{officer.district}</td>
                      <td className="px-4 py-3 text-center font-medium text-gray-800">{officer.total}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                          {officer.submitted}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs font-medium">
                          {officer.draft}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                officer.rate >= 80 ? 'bg-green-500' :
                                officer.rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${officer.rate}%` }}
                            />
                          </div>
                          <span className={`text-xs font-medium ${
                            officer.rate >= 80 ? 'text-green-600' :
                            officer.rate >= 50 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {officer.rate}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => {
                            setOfficerFilter(String(officer.id));
                            setActiveTab('reports');
                          }}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          View Reports
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Report Detail Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-screen overflow-y-auto">

            <div className="bg-blue-900 text-white p-6 rounded-t-xl">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold">Program Monitoring Report</h2>
                  <p className="text-blue-200 text-sm mt-1">BBSHRRDB — Universities & Boards Department</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleExportPMR(selectedReport)}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm"
                  >
                    <Download size={14} />
                    Export PDF
                  </button>
                  <button
                    onClick={() => { setSelectedReport(null); setReportDetail(null); }}
                    className="text-white hover:text-blue-200 text-2xl font-bold"
                  >×</button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
                <div>
                  <span className="text-blue-300">Book No:</span>
                  <span className="ml-2 font-medium">{selectedReport.book_no || '—'}</span>
                </div>
                <div>
                  <span className="text-blue-300">PMR No:</span>
                  <span className="ml-2 font-medium">{selectedReport.pmr_no || '—'}</span>
                </div>
                <div>
                  <span className="text-blue-300">Wing:</span>
                  <span className="ml-2 font-medium">{selectedReport.wing || '—'}</span>
                </div>
                <div>
                  <span className="text-blue-300">Date:</span>
                  <span className="ml-2 font-medium">{formatDate(selectedReport.visit_date)}</span>
                </div>
                <div>
                  <span className="text-blue-300">Start Time:</span>
                  <span className="ml-2 font-medium">{formatTime(selectedReport.start_time)}</span>
                </div>
                <div>
                  <span className="text-blue-300">End Time:</span>
                  <span className="ml-2 font-medium">{formatTime(selectedReport.end_time)}</span>
                </div>
              </div>
            </div>

            <div className="p-6">
              {loadingDetail ? (
                <div className="text-center py-8 text-gray-500">Loading report details...</div>
              ) : reportDetail ? (
                <div className="space-y-6">

                  <div>
                    <h3 className="font-bold text-gray-800 bg-gray-100 px-4 py-2 rounded-lg mb-3">
                      1. Profile of Institute
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm px-2">
                      <div>
                        <span className="text-gray-500">Institution:</span>
                        <span className="ml-2 font-medium">{reportDetail.report?.institution_name}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">District:</span>
                        <span className="ml-2 font-medium">{reportDetail.report?.district_name}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Sector:</span>
                        <span className="ml-2 font-medium">{reportDetail.report?.training_sector}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Training Month:</span>
                        <span className="ml-2 font-medium">{reportDetail.report?.training_month_number}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Visits This Month:</span>
                        <span className="ml-2 font-medium">{reportDetail.report?.visits_this_month}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Total Visits:</span>
                        <span className="ml-2 font-medium">{reportDetail.report?.total_visits_so_far}</span>
                      </div>
                    </div>
                  </div>

                  {reportDetail.sections?.attendance?.length > 0 && (
                    <div>
                      <h3 className="font-bold text-gray-800 bg-gray-100 px-4 py-2 rounded-lg mb-3">
                        2. Attendance & Stipend
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-gray-200">
                          <thead>
                            <tr className="bg-blue-50">
                              <th className="border border-gray-200 px-2 py-2 text-left">Trade</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Tutors Present</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Present (Date)</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Present (Visit)</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Absent</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Stipend</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">ID Cards</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportDetail.sections.attendance.map((row, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="border border-gray-200 px-2 py-2 font-medium">{row.course_name}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.tutors_present}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.trainees_present_on_date}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.trainees_present_current_visit}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.trainees_absent}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.trainees_received_stipend}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.trainees_displaying_id}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {reportDetail.sections?.academic?.length > 0 && (
                    <div>
                      <h3 className="font-bold text-gray-800 bg-gray-100 px-4 py-2 rounded-lg mb-3">
                        3. Academic Progress
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-gray-200">
                          <thead>
                            <tr className="bg-blue-50">
                              <th className="border border-gray-200 px-2 py-2 text-left">Trade</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Weeks</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Scheduled Activity</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Current Topic</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Completed %</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Remaining %</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Activity Plan</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Manual</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportDetail.sections.academic.map((row, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="border border-gray-200 px-2 py-2 font-medium">{row.course_name}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.weeks_completed}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.scheduled_activity}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.current_topic}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">
                                  <span className="text-green-600 font-medium">{row.progress_completed_percent}%</span>
                                </td>
                                <td className="border border-gray-200 px-2 py-2 text-center">
                                  <span className="text-orange-500 font-medium">{row.progress_remaining_percent}%</span>
                                </td>
                                <td className="border border-gray-200 px-2 py-2 text-center">
                                  {row.activity_plan_available ? <span className="text-green-600">✅ Yes</span> : <span className="text-red-500">❌ No</span>}
                                </td>
                                <td className="border border-gray-200 px-2 py-2 text-center">
                                  {row.training_manual_available ? <span className="text-green-600">✅ Yes</span> : <span className="text-red-500">❌ No</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {reportDetail.sections?.examination?.length > 0 && (
                    <div>
                      <h3 className="font-bold text-gray-800 bg-gray-100 px-4 py-2 rounded-lg mb-3">
                        4. Test / Examination
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-gray-200">
                          <thead>
                            <tr className="bg-blue-50">
                              <th className="border border-gray-200 px-2 py-2 text-left">Trade</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Tests Planned</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Conducted</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Appeared</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Passed</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Pass Rate</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Comments</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportDetail.sections.examination.map((row, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="border border-gray-200 px-2 py-2 font-medium">{row.course_name}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.tests_planned}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.tests_conducted}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.trainees_appeared}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.trainees_passed}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">
                                  <span className={`font-medium ${
                                    row.trainees_appeared > 0 && (row.trainees_passed / row.trainees_appeared) >= 0.7
                                      ? 'text-green-600' : 'text-red-500'}`}>
                                    {row.trainees_appeared > 0
                                      ? `${Math.round((row.trainees_passed / row.trainees_appeared) * 100)}%`
                                      : '—'}
                                  </span>
                                </td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.comments || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {reportDetail.sections?.facilities?.length > 0 && (
                    <div>
                      <h3 className="font-bold text-gray-800 bg-gray-100 px-4 py-2 rounded-lg mb-3">
                        5. Facilities Gradation
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-gray-200">
                          <thead>
                            <tr className="bg-blue-50">
                              <th className="border border-gray-200 px-2 py-2 text-left">Trade</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Tutor</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Manual</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Equipment</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Climate</th>
                              <th className="border border-gray-200 px-2 py-2 text-center">Facilities</th>
                              <th className="border border-gray-200 px-2 py-2 text-center font-bold">Total/25</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportDetail.sections.facilities.map((row, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="border border-gray-200 px-2 py-2 font-medium">{row.course_name}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.tutor_capacity_score}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.training_manual_score}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.training_material_score}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.learning_climate_score}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center">{row.facilities_score}</td>
                                <td className="border border-gray-200 px-2 py-2 text-center font-bold text-blue-800">{row.grand_total}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {reportDetail.report?.overall_assessment && (
                    <div>
                      <h3 className="font-bold text-gray-800 bg-gray-100 px-4 py-2 rounded-lg mb-3">
                        6. Concluding Comments
                      </h3>
                      <p className="text-sm text-gray-700 px-2">{reportDetail.report.overall_assessment}</p>
                    </div>
                  )}

                  <div className="border-t pt-4">
                    <h3 className="font-bold text-gray-800 bg-gray-100 px-4 py-2 rounded-lg mb-3">Signatures</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="border border-gray-200 rounded-lg p-4 text-center">
                        <p className="text-gray-500 text-xs mb-6">Focal Person / Principal Signature</p>
                        <div className="h-12 flex items-center justify-center text-gray-400 text-xs">
                          {reportDetail.report?.focal_person_signature_url ? 'Signed' : 'No signature uploaded'}
                        </div>
                      </div>
                      <div className="border border-gray-200 rounded-lg p-4 text-center">
                        <p className="text-gray-500 text-xs mb-6">Monitoring Officer Signature</p>
                        <div className="h-12 flex items-center justify-center text-gray-400 text-xs">
                          {reportDetail.report?.officer_signature_url ? 'Signed' : 'No signature uploaded'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4 flex justify-between text-sm">
                    <div>
                      <span className="text-gray-500">Submitted by:</span>
                      <span className="ml-2 font-medium">{reportDetail.report?.officer_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Submitted at:</span>
                      <span className="ml-2 font-medium">{formatTime(reportDetail.report?.submitted_at)}</span>
                    </div>
                  </div>

                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default Reports;